import { CustomerBillingModel } from 'api-hooks/customer';
import { SaleTransactionType } from 'api-hooks/refund/refund';
import { Option } from 'baseui/select';
import { NavigationRoutes } from 'common/constants';
import { transformLabel } from 'common/utils/string';
import notification from 'components/common/notification';
import { Input } from 'components/elements';
import { Button } from 'components/elements/buttons';
import Text from 'components/elements/text';
import useNavigation from 'hooks/use-navigation';
import CustomerSelectInput from 'legacy-modules/master/customer/components/select-input';
import EmployeeSelectInput from 'legacy-modules/master/select-input/employee-select-input';
import CustomerBillingSelectInput from 'legacy-modules/sales/component/customer-billing-select-input';
import {
  CardSection,
  FullContainer,
  HalfContainer,
  OneThirdContainer,
  SectionContainer,
  SeparatorVertical,
} from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

interface Props {}

function transformer(item: CustomerBillingModel): Option {
  return {
    id: item.id,
    label: transformLabel([item.name, item.address, item.phone]),
  };
}

export default function CustomerDetailField(props: Props) {
  const { t } = useTranslation();

  const { control, setValue } = useFormContext();
  const { navigate } = useNavigation();

  // const [billingOptions, setBillingOptions] = React.useState<Option[]>([]);

  const keys = 'releaseSale.id';

  const [transactionSaleType, invoiceNumber] = useWatch({
    control,
    name: ['transactionSaleType', keys],
  });

  const onItemInvoked = React.useCallback(() => {
    let target = '';

    switch (transactionSaleType) {
      case SaleTransactionType.ACTIVITY:
        target = NavigationRoutes.ActivitySalesView;
        break;
      case SaleTransactionType.CRUISE:
        target = NavigationRoutes.CruiseSalesView;
        break;
      case SaleTransactionType.DOCUMENT:
        target = NavigationRoutes.TravelDocumentSalesView;
        break;
      case SaleTransactionType.FLIGHT:
        target = NavigationRoutes.TicketSalesView;
        break;
      case SaleTransactionType.HOTEL:
        target = NavigationRoutes.HotelSalesView;
        break;
      case SaleTransactionType.PRODUCT:
        target = NavigationRoutes.ProductSalesView;
        break;
      case SaleTransactionType.TOUR:
        target = NavigationRoutes.ToursalesView;
        break;
      default:
        notification.error({
          message: t('refund:no_selected_extra', {
            extra: t('common:invoice_number'),
          }),
        });
        return <></>;
    }

    navigate(target as NavigationRoutes, {
      id: invoiceNumber,
    });
  }, [transactionSaleType, navigate, invoiceNumber, t]);

  return (
    <CardSection style={{ paddingBottom: 0 }}>
      <FullContainer>
        <SectionContainer $style={{ alignItem: 'center' }}>
          <div style={{ flex: 1 }}>
            <Text variant="HeadingXSmall">{t('common:sales_details')}</Text>
          </div>
          <SeparatorVertical $margin={8} />
          <Button buttonType="secondary" onClick={onItemInvoked}>
            {t('common:view_detail')}
          </Button>
        </SectionContainer>
        <SeparatorVertical $margin={36} />

        <SectionContainer>
          <HalfContainer>
            <Input
              type="text"
              name="releaseSale.previousSale.code"
              label={t('common:group_code')}
              placeholder={t('common:group_code')}
              disabled
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="text"
              name="releaseSale.previousSale.description"
              label={t('common:description')}
              placeholder={t('common:description')}
              disabled
            />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <Input
              type="date-time"
              name="releaseSale.previousSale.transactionAt"
              label={t('common:transaction_at')}
              placeholder={t('common:transaction_at')}
              disabled
            />
          </HalfContainer>
          <HalfContainer>
            <Input
              type="number"
              name="releaseSale.previousSale.total"
              label={t('common:total')}
              placeholder={t('common:total')}
              disabled
            />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <HalfContainer>
            <CustomerSelectInput
              name="releaseSale.previousSale.customerId"
              onAfterDetailChange={(params) => {
                const _options = params.billings.map((billing) =>
                  transformer(billing),
                );
                setValue('customerBillingOptions', _options);
              }}
              disabled
            />
          </HalfContainer>
          <HalfContainer>
            <CustomerBillingSelectInput
              name="releaseSale.previousSale.customerBillingId"
              disabled
            />
          </HalfContainer>
        </SectionContainer>
        <SectionContainer>
          <OneThirdContainer>
            <EmployeeSelectInput
              name="releaseSale.previousSale.adminId"
              label={t('common:admin')}
              placeholder={t('common:admin')}
              disabled
            />
          </OneThirdContainer>
          <OneThirdContainer>
            <EmployeeSelectInput
              name="releaseSale.previousSale.operatorId"
              label={t('common:operator')}
              placeholder={t('common:operator')}
              disabled
            />
          </OneThirdContainer>
          <OneThirdContainer>
            <EmployeeSelectInput
              name="releaseSale.previousSale.salesmanId"
              label={t('common:sales')}
              placeholder={t('common:sales')}
              disabled
            />
          </OneThirdContainer>
        </SectionContainer>
      </FullContainer>
    </CardSection>
  );
}
